import React from 'react'
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { Logo, useUserService } from '@climate/components'
import Avatar from 'react-avatar'
import type { UserNavigationItem } from '../../types'

import LogoImage from '../../../assets/images/logo.png'

function Navbar () {
  const { user, _logout } = useUserService(window.config.iam_service_url, window.config.session_cookie, 'iam')

  const userNavigation: UserNavigationItem[] = [
    {
      name: 'Contact support', onClick: (e?: React.MouseEvent) => {
        window.location.href = 'mailto:support@tellusmarkets.com'
        e?.preventDefault()
      }
    },
    { name: 'Sign out', onClick: _logout }
  ].filter(Boolean) as UserNavigationItem[]

  return (
    <Disclosure as="nav">
      {({ open }) => (
        <>
          <div className="bg-white border">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between h-16">
              <div className="flex">
                <div className="shrink-0 flex items-center">
                  <Logo LogoImage={LogoImage} alt="Tellus Iam logo" />
                </div>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:items-center">
                {/* Profile dropdown */}
                <Menu as="div" className="ml-3 relative">
                  {({ open }) => (
                    <>
                      <div data-cy="profile-button">
                        <MenuButton
                          className="bg-white flex text-sm rounded-full focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-perfect-shine">
                          <Avatar color="transparent" className="bg-pacific-storm" name={user.full_name}
                            textSizeRatio={2} size={'40'} round={true} />
                        </MenuButton>
                      </div>
                      <Transition
                        show={open}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <MenuItems
                          static
                          className="z-50 origin-top-right absolute right-0 mt-2 w-52 rounded-md shadow-lg bg-white ring-1 ring-black/5 focus:outline-hidden"
                        >
                          <MenuItem as="span"
                            className="truncate cursor-default text-white rounded-t-md bg-pacific-storm block px-4 pt-2 text-xs">
                            {user.full_name}
                          </MenuItem>
                          <MenuItem as="span"
                            className="truncate cursor-default text-white bg-pacific-storm block px-4 pb-2 text-xs">
                            {user.entity_name}
                          </MenuItem>
                          {userNavigation.map((item) => (
                            <React.Fragment key={item.name}>
                              <MenuItem>
                                <a
                                  data-cy={`${item.name.replace(/\s+/g, '-').toLowerCase()}-menu-item`}
                                  href={item.href}
                                  onClick={item.onClick}
                                  className="block px-4 py-2 text-sm text-gray-700 cursor-pointer data-focus:bg-gray-100"
                                >
                                  {item.name}
                                </a>
                              </MenuItem>
                            </React.Fragment>
                          ))}
                        </MenuItems>
                      </Transition>
                    </>
                  )}
                </Menu>
              </div>
              <div className="-mr-2 flex items-center sm:hidden">
                {/* Mobile menu button */}
                <DisclosureButton
                  className="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-perfect-shine">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              </div>
            </div>
          </div>
          <div className="hidden sm:flex h-16">
            {/* subnav height holder */}
          </div>


          <DisclosurePanel className="sm:hidden">
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="flex items-center px-4">
                <div className="shrink-0">
                  <Avatar color="transparent" className="bg-pacific-storm focus:ring-perfect-shine"
                    name={user.full_name} textSizeRatio={2} size={'40'} round={true} />
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">{user.name as string}</div>
                  <div className="text-sm font-medium text-gray-500">{user.email as string}</div>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {userNavigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={item.onClick}
                    className="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 cursor-pointer"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
}

export default Navbar
